import { useRef, useState } from 'react';
import api, { tasksAPI } from '../../services/api';
import { Task } from './types';

interface TaskFormData {
  name: string;
  description: string;
  goalId: number | null;
  categoryId: number | null;
  energy: '1' | '2' | '3';  // 1=А, 2=Б, 3=В
  sprint: string;
  dueDate: string;
  dueTime: string;
}

export const useTaskOperations = (
  loadData: () => Promise<any>,
  applyTaskUpdates?: (updates: Array<Partial<Task> & { id: number }>) => void
) => {
  const taskWrites = useRef(new Map<number, Promise<void>>());
  const orderWrite = useRef<Promise<void>>(Promise.resolve());
  const trackTaskWrite = (id: number, next: Promise<void>) => {
    taskWrites.current.set(id, next);
    const cleanup = () => { if (taskWrites.current.get(id) === next) taskWrites.current.delete(id); };
    void next.then(cleanup, cleanup);
    return next;
  };
  const queueTaskWrite = (id: number, save: () => Promise<void>) => {
    const previous = taskWrites.current.get(id);
    const next = (previous || Promise.resolve()).catch(() => {}).then(save);
    return trackTaskWrite(id, next);
  };
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [selectedGoal, setSelectedGoal] = useState<number | null | 'none'>(null);
  const [filterEnergy, setFilterEnergy] = useState<string>('all');
  const [filterSprint, setFilterSprint] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'archived'>('active');
  const [filterCategory, setFilterCategory] = useState<number | 'all'>('all');
  
  const [taskForm, setTaskForm] = useState<TaskFormData>({
    name: '',
    description: '',
    goalId: null,
    categoryId: null,
    energy: '2',
    sprint: '',
    dueDate: '',
    dueTime: ''
  });

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const taskData = {
        ...taskForm,
        goalId: taskForm.goalId || null,
        categoryId: taskForm.categoryId || null,
        sprint: taskForm.sprint || null,
        dueDate: taskForm.dueDate || null,
        dueTime: taskForm.dueTime || null
      };
      await api.post('/api/tasks', taskData);
      setTaskForm({ name: '', description: '', goalId: null, categoryId: null, energy: '2', sprint: '', dueDate: '', dueTime: '' });
      setShowTaskForm(false);
      await loadData();
    } catch (error: any) {
      console.error('Failed to create task:', error);
      alert(`Ошибка при создании задачи: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleUpdateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTaskId) return;

    try {
      const taskData = {
        ...taskForm,
        goalId: taskForm.goalId || null,
        categoryId: taskForm.categoryId || null,
        sprint: taskForm.sprint || null,
        dueDate: taskForm.dueDate || null,
        dueTime: taskForm.dueTime || null,
        // Если убрали цель, то автоматически убираем stepId (дело не может быть в шаге без цели)
        stepId: taskForm.goalId ? undefined : null
      };
      await api.put(`/api/tasks/${editingTaskId}`, taskData);
      setTaskForm({ name: '', description: '', goalId: null, categoryId: null, energy: '2', sprint: '', dueDate: '', dueTime: '' });
      setShowTaskForm(false);
      setEditingTaskId(null);
      await loadData();
    } catch (error: any) {
      console.error('Failed to update task:', error);
      alert(`Ошибка при обновлении задачи: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTaskId(task.id);
    setTaskForm({
      name: task.name,
      description: task.description || '',
      goalId: task.goalId,
      categoryId: task.categoryId || null,
      energy: task.energy,
      sprint: task.sprint || '',
      dueDate: task.dueDate || '',
      dueTime: task.dueTime || ''
    });
    // Не открываем верхнюю форму - редактирование теперь inline на карточке
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setTaskForm({ name: '', description: '', goalId: null, categoryId: null, energy: '2', sprint: '', dueDate: '', dueTime: '' });
    setShowTaskForm(false);
  };

  const handleDeleteTask = async (taskId: number) => {
    if (window.confirm('Удалить эту задачу?')) {
      try {
        await api.delete(`/api/tasks/${taskId}`);
        await loadData();
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  const handleUpdateTaskStatus = async (
    taskId: number, 
    newStatus: Task['status'], 
    completedAt?: string | null
  ) => queueTaskWrite(taskId, async () => {
    try {
      const updateData: any = { status: newStatus };
      
      // Если переводим в done или archived, сохраняем дату завершения
      if (newStatus === 'done' || newStatus === 'archived') {
        updateData.completedAt = completedAt || new Date().toISOString();
      }
      
      // Если возвращаем из архива, очищаем completedAt
      if (newStatus !== 'done' && newStatus !== 'archived') {
        updateData.completedAt = null;
      }
      
      const response = await api.put(`/api/tasks/${taskId}`, updateData);
      const saved = response.data;
      // A status response may contain an older order from a concurrent reorder.
      // Commit only the fields this request actually changed.
      applyTaskUpdates?.([{
        id: taskId,
        status: saved?.status ?? newStatus,
        completedAt: saved?.completedAt !== undefined ? saved.completedAt : updateData.completedAt,
        ...(saved?.updatedAt ? { updatedAt: saved.updatedAt } : {})
      }]);
    } catch (error) {
      console.error('Failed to update task:', error);
      // Вызывающий код показывает ошибку и откатывает оптимистичные изменения.
      throw error;
    }
  });

  const handleReorderTasks = (taskIds: number[], movedTask?: { id: number; status: Task['status'] }) => {
    const previousTask = movedTask && taskWrites.current.get(movedTask.id);
    const next = orderWrite.current.catch(() => {}).then(async () => {
      await previousTask?.catch(() => {});
      const move = movedTask && {
        ...movedTask,
        completedAt: ['done', 'archived'].includes(movedTask.status) ? new Date().toISOString() : null
      };
      const response = await tasksAPI.reorder(taskIds, move);
      applyTaskUpdates?.(response.updated);
    });
    orderWrite.current = next;
    return movedTask ? trackTaskWrite(movedTask.id, next) : next;
  };

  return {
    showTaskForm,
    setShowTaskForm,
    editingTaskId,
    setEditingTaskId,
    selectedGoal,
    setSelectedGoal,
    filterEnergy,
    setFilterEnergy,
    filterSprint,
    setFilterSprint,
    filterStatus,
    setFilterStatus,
    filterCategory,
    setFilterCategory,
    taskForm,
    setTaskForm,
    handleCreateTask,
    handleUpdateTask,
    handleEditTask,
    handleCancelEdit,
    handleDeleteTask,
    handleUpdateTaskStatus,
    handleReorderTasks
  };
};
